import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { accountingApi } from '../../services/accountingApi';
import { AccountingAccount, Expense, ExpenseCategory } from '../../types/accounting';
import '../../pages/Accounting.css';

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  expense: Expense | null;
  categories: ExpenseCategory[];
  accounts: AccountingAccount[];
}

const ExpenseModal: React.FC<ExpenseModalProps> = ({ isOpen, onClose, onSaved, expense, categories, accounts }) => {
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [payee, setPayee] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (expense) {
      setCategoryId(String(expense.category_id));
      setAccountId(String(expense.account_id));
      setPayee(expense.payee || '');
      setAmount(String(expense.amount));
      setExpenseDate(expense.expense_date.split('T')[0]);
      setDescription(expense.description || '');
    } else {
      setCategoryId(categories[0] ? String(categories[0].id) : '');
      setAccountId(accounts[0] ? String(accounts[0].id) : '');
      setPayee('');
      setAmount('');
      setExpenseDate(new Date().toISOString().split('T')[0]);
      setDescription('');
    }
    setErrors({});
  }, [isOpen, expense, categories, accounts]);

  if (!isOpen) return null;

  const validate = () => {
    const next: Record<string, string> = {};
    if (!categoryId) next.categoryId = 'Category is required';
    if (!accountId) next.accountId = 'Account is required';
    if (!amount || parseFloat(amount) <= 0) next.amount = 'Amount must be greater than 0';
    if (!expenseDate) next.expenseDate = 'Date is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      categoryId: parseInt(categoryId),
      accountId: parseInt(accountId),
      payee: payee.trim() || undefined,
      amount: parseFloat(amount),
      expenseDate,
      description: description.trim() || undefined
    };

    try {
      setLoading(true);
      if (expense) {
        await accountingApi.updateExpense(expense.id, payload);
        toast.success('Expense updated successfully');
      } else {
        await accountingApi.createExpense(payload);
        toast.success('Expense recorded successfully');
      }
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.error || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="acc-modal-overlay" onClick={onClose}>
      <div className="acc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acc-modal-header">
          <h2>{expense ? 'Edit Expense' : 'Add Expense'}</h2>
          <button className="acc-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="acc-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="acc-form-row">
              <div className="acc-form-group">
                <label>Category <span className="acc-required">*</span></label>
                <select className={errors.categoryId ? 'error' : ''} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={loading}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {errors.categoryId && <div className="acc-error-message">{errors.categoryId}</div>}
              </div>
              <div className="acc-form-group">
                <label>Account <span className="acc-required">*</span></label>
                <select className={errors.accountId ? 'error' : ''} value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={loading}>
                  <option value="">Select account</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {errors.accountId && <div className="acc-error-message">{errors.accountId}</div>}
              </div>
            </div>

            <div className="acc-form-row">
              <div className="acc-form-group">
                <label>Amount (AED) <span className="acc-required">*</span></label>
                <input type="number" step="0.01" min="0" className={errors.amount ? 'error' : ''} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={loading} placeholder="0.00" />
                {errors.amount && <div className="acc-error-message">{errors.amount}</div>}
              </div>
              <div className="acc-form-group">
                <label>Date <span className="acc-required">*</span></label>
                <input type="date" className={errors.expenseDate ? 'error' : ''} value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} disabled={loading} max={new Date().toISOString().split('T')[0]} />
                {errors.expenseDate && <div className="acc-error-message">{errors.expenseDate}</div>}
              </div>
            </div>

            <div className="acc-form-group">
              <label>Payee / Paid To</label>
              <input type="text" value={payee} onChange={(e) => setPayee(e.target.value)} disabled={loading} placeholder="e.g. therapist name, vendor, landlord" maxLength={200} />
            </div>

            <div className="acc-form-group">
              <label>Description / Notes</label>
              <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} disabled={loading} maxLength={500} placeholder="Optional details about this expense" />
            </div>

            <div className="acc-form-actions">
              <button type="button" className="acc-btn acc-btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
              <button type="submit" className="acc-btn acc-btn-primary" disabled={loading}>{loading ? 'Saving...' : (expense ? 'Save Changes' : 'Add Expense')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExpenseModal;
